import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Spinner } from '@/components/ui';
import { api } from '@/lib/api';
import {
  getStoredLoansAppRole,
  isLoansAppRole,
  setStoredLoansAppRole,
  syncLoansAppRoleFromLocation,
} from '@/lib/app-role';
import { APP_SELECTOR_RETURN_TO, exchangeAuthCode } from '@/lib/keycloak';
import { getAppSelectorUrl } from '@/lib/runtime-urls';
import { setStoredActiveTenantId } from '@/lib/tenant-context';
import { useAuthStore } from '@/stores/auth-store';
import type { User } from '@/types';

const APP_SELECTOR_URL = getAppSelectorUrl();

async function fetchCurrentLoansUser(requestedAppRole: string | null) {
  return api.get<User>('/auth/me', {
    headers: requestedAppRole
      ? {
          'X-App-Role': requestedAppRole,
        }
      : undefined,
    skipTenantContext: true,
    skipAuthRefresh: true,
  } as any);
}

export function AuthCallback() {
  const navigate = useNavigate();
  const login = useAuthStore(s => s.login);
  const [error, setError] = useState<string | null>(null);
  const hasStarted = useRef(false);

  useEffect(() => {
    if (!error) {
      return;
    }

    const timer = window.setTimeout(() => {
      window.location.replace(APP_SELECTOR_URL);
    }, 300);

    return () => window.clearTimeout(timer);
  }, [error]);

  useEffect(() => {
    if (hasStarted.current) {
      return;
    }

    hasStarted.current = true;

    const run = async () => {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const errorMsg = urlParams.get('error');
        if (errorMsg) {
          setError(errorMsg);
          return;
        }

        const code = urlParams.get('code');
        const state = urlParams.get('state');
        if (!code || !state) {
          setError('No Keycloak authorization code received');
          return;
        }

        const tokenSet = await exchangeAuthCode(code, state);
        const requestedRoleFromReturnTo = new URLSearchParams(
          tokenSet.returnTo.split('?')[1] || '',
        ).get('app-role');
        const storedAppRole = getStoredLoansAppRole();
        const requestedAppRole = isLoansAppRole(requestedRoleFromReturnTo)
          ? requestedRoleFromReturnTo
          : storedAppRole;

        if (requestedAppRole) {
          setStoredLoansAppRole(requestedAppRole);
          setStoredActiveTenantId(null, {
            appRole: requestedAppRole,
          });
        } else {
          syncLoansAppRoleFromLocation();
        }

        let userResponse;
        try {
          userResponse = await fetchCurrentLoansUser(requestedAppRole);
        } catch (firstError: any) {
          if (firstError?.response?.status !== 401) {
            throw firstError;
          }

          await new Promise(resolve => window.setTimeout(resolve, 150));
          userResponse = await fetchCurrentLoansUser(requestedAppRole);
        }
        const { data: user } = userResponse;
        setStoredLoansAppRole(user.appRole || null);

        login(user, tokenSet.expiresIn);

        if (tokenSet.returnTo === APP_SELECTOR_RETURN_TO) {
          window.location.replace(APP_SELECTOR_URL);
          return;
        }

        navigate(tokenSet.returnTo || '/dashboard', { replace: true });
      } catch (err: any) {
        setError(err.message || 'Failed to complete Keycloak sign-in');
      }
    };

    void run();
  }, [login, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center text-gray-500">
        <Spinner size="lg" className="w-6 h-6 animate-spin mx-auto mb-2" />
        <p className="text-sm">
          {error ? 'Redirecting to app selector…' : 'Completing Keycloak sign-in…'}
        </p>
      </div>
    </div>
  );
}
